import type { PlaybookSelection } from "@repo/app-types/entities/playbook";
import type {
  SignalDb,
  SignalMetadataSchema,
} from "@repo/app-types/entities/signal";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { linkStyle } from "@repo/ui/Link/index";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { IconArrowLeft, IconChevronDown } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { DialogTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { CopilotProposalBanner } from "../../../components/recordingAssessmentEditor/copilotProposalBanner.js";
import { FieldDiffView } from "../../../components/recordingAssessmentEditor/fieldDiffView.js";
import { MetadataSchemaBuilder } from "./metadataSchemaBuilder.js";
import { SignalPlaybookScopeField } from "./signalPlaybookScopeField.js";
import {
  SignalTestResultPill,
  type SignalTestResultPillState,
} from "./signalTestResultPill.js";

export interface SignalEditorContainerProps {
  name: string;
  description: string;
  metadataSchema: SignalMetadataSchema;
  playbookScope: PlaybookSelection;
  error: string | null;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onMetadataSchemaChange: (value: SignalMetadataSchema) => void;
  onPlaybookScopeChange: (value: PlaybookSelection) => void;
  onClose: () => void;
  onDiscard?: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  isSubmitDisabled: boolean;
  submitLabel: string;
  showDiscard: boolean;
  onTestSignalClick: () => void;
  onConfigureTestClick: () => void;
  isTestSignalDisabled: boolean;
  testResultPillState: SignalTestResultPillState | null;
  onViewMatches: () => void;
  /**
   * A pending copilot proposal loaded into the form: the banner offers Apply /
   * Discard, and the changed fields can be diffed against the saved signal.
   */
  suggestion?: {
    isNewSignal: boolean;
    /** The saved signal the proposal changes; absent for a new signal. */
    baseline:
      | Pick<SignalDb, "name" | "description" | "metadata_schema">
      | undefined;
    onDiscard: () => void;
    onApply: () => void;
    /** A decision is in flight; both are held until it lands. */
    isResolving: boolean;
  };
}

type FieldView = "edit" | "diff";

/** The fields a proposed schema adds or changes against the saved one. */
function findChangedMetadataFieldIds(
  proposed: SignalMetadataSchema,
  baseline: SignalMetadataSchema | undefined,
): ReadonlySet<string> {
  const baselineById = new Map(
    (baseline?.fields ?? []).map((field) => [field.id, field]),
  );
  return new Set(
    proposed.fields
      .filter(
        (field) =>
          JSON.stringify(baselineById.get(field.id)) !== JSON.stringify(field),
      )
      .map((field) => field.id),
  );
}

export const SignalEditorContainer: FC<SignalEditorContainerProps> = ({
  name,
  description,
  metadataSchema,
  playbookScope,
  error,
  onNameChange,
  onDescriptionChange,
  onMetadataSchemaChange,
  onPlaybookScopeChange,
  onClose,
  onDiscard,
  onSubmit,
  isLoading,
  isSubmitDisabled,
  submitLabel,
  showDiscard,
  onTestSignalClick,
  onConfigureTestClick,
  isTestSignalDisabled,
  testResultPillState,
  onViewMatches,
  suggestion,
}) => {
  const baseline = suggestion?.baseline;
  const [nameView, setNameView] = useState<FieldView>("edit");
  const [descriptionView, setDescriptionView] = useState<FieldView>("edit");
  const showNameDiffToggle = baseline !== undefined && baseline.name !== name;
  const showDescriptionDiffToggle =
    baseline !== undefined && baseline.description !== description;
  const effectiveNameView = showNameDiffToggle ? nameView : "edit";
  const effectiveDescriptionView = showDescriptionDiffToggle
    ? descriptionView
    : "edit";
  const highlightedFieldIds = suggestion
    ? findChangedMetadataFieldIds(metadataSchema, baseline?.metadata_schema)
    : undefined;

  const renderDiffToggle = (
    view: FieldView,
    setView: (view: FieldView) => void,
  ) => (
    <Button
      variant={ButtonVariant.Tertiary}
      size={Size.MEDIUM}
      className={twMerge("cursor-pointer pr-0", linkStyle())}
      onClick={() => setView(view === "edit" ? "diff" : "edit")}
    >
      {view === "diff" ? "Edit" : "See changes"}
    </Button>
  );

  return (
    <div className="flex h-full flex-col">
      {/* Top toolbar */}
      <div className="flex items-center justify-between border-b border-border-light bg-white px-4 py-2">
        <Button
          variant={ButtonVariant.Tertiary}
          onClick={onClose}
          icon={<IconArrowLeft className="h-4 w-4" />}
          size={Size.MEDIUM}
        >
          Back
        </Button>
        <div className="flex items-center gap-2">
          <div className="flex items-stretch">
            <Button
              variant={ButtonVariant.Secondary}
              onClick={onTestSignalClick}
              isDisabled={isTestSignalDisabled}
              size={Size.MEDIUM}
              className="rounded-r-none"
            >
              Test signal
            </Button>
            <DialogTrigger>
              <Button
                variant={ButtonVariant.Secondary}
                icon={<IconChevronDown className="h-4 w-4" />}
                isDisabled={isTestSignalDisabled}
                size={Size.MEDIUM}
                className="-ml-px rounded-l-none px-2"
              />
              <SimpleMenuPopover placement="bottom end">
                <SimpleMenu aria-label="Test signal options">
                  <SimpleMenuItem onAction={onConfigureTestClick}>
                    Configure test…
                  </SimpleMenuItem>
                </SimpleMenu>
              </SimpleMenuPopover>
            </DialogTrigger>
          </div>
          {showDiscard && onDiscard && (
            <Button
              variant={ButtonVariant.Secondary}
              onClick={onDiscard}
              size={Size.MEDIUM}
            >
              Discard
            </Button>
          )}
          <Button
            variant={ButtonVariant.Primary}
            onClick={onSubmit}
            isDisabled={isSubmitDisabled}
            isLoading={isLoading}
            size={Size.MEDIUM}
          >
            {submitLabel}
          </Button>
        </div>
      </div>

      {suggestion ? (
        <CopilotProposalBanner
          className="mx-0 mt-0 rounded-none border-b border-border-light"
          message={
            suggestion.isNewSignal
              ? "Showing a new signal Ask Alpharun suggests"
              : "Showing changes Ask Alpharun suggests"
          }
          discardLabel="Discard changes"
          confirmLabel="Apply changes"
          onDiscard={suggestion.onDiscard}
          onConfirm={suggestion.onApply}
          isDisabled={suggestion.isResolving}
        />
      ) : null}

      {/* Form content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto flex max-w-xl flex-col space-y-6">
          {testResultPillState ? (
            <SignalTestResultPill
              state={testResultPillState}
              onViewMatches={onViewMatches}
            />
          ) : null}

          <div>
            <div className="mb-1 flex min-h-8 items-center justify-between">
              <Label size={Size.SMALL}>Name</Label>
              {showNameDiffToggle
                ? renderDiffToggle(effectiveNameView, setNameView)
                : null}
            </div>
            {effectiveNameView === "diff" && baseline ? (
              <FieldDiffView savedValue={baseline.name} editedValue={name} />
            ) : (
              <Input
                id="signal_name"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="Signal name"
                maxLength={200}
              />
            )}
          </div>

          <div>
            <div className="mb-1 flex min-h-8 items-center justify-between">
              <Label size={Size.SMALL}>Description</Label>
              {showDescriptionDiffToggle
                ? renderDiffToggle(effectiveDescriptionView, setDescriptionView)
                : null}
            </div>
            {effectiveDescriptionView === "diff" && baseline ? (
              <FieldDiffView
                savedValue={baseline.description}
                editedValue={description}
              />
            ) : (
              <Textarea
                id="signal_description"
                value={description}
                onChange={onDescriptionChange}
                placeholder="e.g. Customer mentioning one of our competitors"
                maxLength={5000}
                height="15rem"
              />
            )}
            <FieldDescription>
              A natural language definition of the conversational pattern to
              detect.
            </FieldDescription>
          </div>

          <div className="flex flex-col gap-1">
            <Label size={Size.SMALL}>Playbooks</Label>
            <SignalPlaybookScopeField
              value={playbookScope}
              onChange={onPlaybookScopeChange}
            />
            <FieldDescription>
              The playbooks and voice agents whose calls this signal is detected
              in.
            </FieldDescription>
          </div>

          <div>
            <MetadataSchemaBuilder
              value={metadataSchema}
              onChange={onMetadataSchemaChange}
              highlightedFieldIds={highlightedFieldIds}
            />
          </div>

          {error && <div className="text-sm text-destructive">{error}</div>}
        </div>
      </div>
    </div>
  );
};
